import { MISSION_TYPES } from "./missionTypeConfig";

/**
 * The display contract is deliberately camelCase.  Mission records returned by
 * the API and records assembled by the client can therefore use the same
 * title/location/time rules without translating fields in each view.
 */
export type MissionDisplayInput = {
  missionType?: string | null;
  evenementNom?: string | null;
  eventName?: string | null;
  lieuLibre?: string | null;
  heureDebut?: string | null;
  arrivalTime?: string | null;
  startDate?: string | Date | null;
  journeeEntiere?: boolean | null;
};

export type MissionDisplayAddressSource = {
  address?: string | null;
  adresse?: string | null;
  codePostal?: string | null;
  ville?: string | null;
  pays?: string | null;
  secteurActivite?: string | null;
};

export type MissionDisplayRelated = {
  client?: MissionDisplayAddressSource | null;
  prospect?: MissionDisplayAddressSource | null;
  address?: string | null;
};

function nonBlank(value: unknown): string | null {
  if (typeof value !== "string") return null;
  return value.trim() ? value : null;
}

function trimmedNonBlank(value: unknown): string | null {
  const present = nonBlank(value);
  return present?.trim() || null;
}

export function resolveMissionTierName(source?: {
  nomCommercial?: string | null;
  companyName?: string | null;
  societe?: string | null;
} | null): string | null {
  return nonBlank(source?.nomCommercial) ||
    nonBlank(source?.companyName) ||
    nonBlank(source?.societe);
}

function resolveRelatedAddress(source?: MissionDisplayAddressSource | null): string | null {
  if (!source) return null;
  const explicit = nonBlank(source.address) || nonBlank(source.adresse);
  if (explicit) return explicit;
  if (!nonBlank(source.codePostal) && !nonBlank(source.pays)) return null;
  return [source.codePostal, source.ville, source.pays]
    .map(nonBlank)
    .filter((value): value is string => Boolean(value))
    .join(" ") || null;
}

function typeLabel(mission: MissionDisplayInput): string {
  const type = trimmedNonBlank(mission.missionType);
  if (!type) return "Mission";
  return MISSION_TYPES[type as keyof typeof MISSION_TYPES]?.label || type;
}

export function resolveMissionTitle(
  mission: MissionDisplayInput,
  tierName?: string | null,
): string {
  return (
    nonBlank(mission.evenementNom) ||
    nonBlank(mission.eventName) ||
    nonBlank(tierName) ||
    typeLabel(mission)
  );
}

/**
 * Resolve the address consistently for every consumer.
 *
 * The string overload is kept for existing callers and tests.  New callers
 * should pass the related client/prospect object so the fallback is assembled
 * here rather than independently in each view.
 */
export function resolveMissionLocation(
  mission: MissionDisplayInput,
  fallback?: string | null | MissionDisplayRelated,
): string | null {
  const explicit = nonBlank(mission.lieuLibre);
  if (explicit) return explicit;

  if (typeof fallback === "string" || fallback == null) {
    return nonBlank(fallback);
  }

  const relatedAddress =
    nonBlank(fallback.address) ||
    resolveRelatedAddress(fallback.prospect) ||
    resolveRelatedAddress(fallback.client);
  if (relatedAddress) return relatedAddress;

  const prospect = fallback.prospect;
  if (prospect) {
    return [prospect.ville, prospect.secteurActivite]
      .map(nonBlank)
      .filter((value): value is string => Boolean(value))
      .join(", ") || null;
  }

  return null;
}

export function resolveMissionEffectiveStart(
  mission: MissionDisplayInput,
): string | null {
  const explicitTime =
    trimmedNonBlank(mission.heureDebut) || trimmedNonBlank(mission.arrivalTime);
  if (explicitTime) return explicitTime;
  if (!mission.startDate) return null;

  const date = new Date(mission.startDate);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

export function compareMissions(
  a: MissionDisplayInput,
  b: MissionDisplayInput,
): number {
  if (a.journeeEntiere !== b.journeeEntiere) {
    return a.journeeEntiere ? -1 : 1;
  }

  const aStart = resolveMissionEffectiveStart(a);
  const bStart = resolveMissionEffectiveStart(b);
  if (aStart !== bStart) {
    if (aStart === null) return 1;
    if (bStart === null) return -1;
    const timeOrder = aStart.localeCompare(bStart);
    if (timeOrder !== 0) return timeOrder;
  }

  return resolveMissionTitle(a).localeCompare(resolveMissionTitle(b), "fr");
}